import React, { useState } from "react";
import { createPortal } from "react-dom";

const SortOrderHeader = ({ displayName, sortField, setSortOrderParent }) => {
  const [sortOrder, setSortOrder] = useState(null);

  const handleClickSort = () => {
    const newSort = sortOrder === "asc" ? "desc" : sortOrder === "desc" ? null : "asc";
    setSortOrder(newSort);
    setSortOrderParent?.(newSort ? { sort_by: sortField, sort_type: newSort } : null);
  };

  return (
    <div
      className="flex items-center gap-2 justify-content-between cursor-pointer select-none w-full"
      onClick={handleClickSort}
      title={`Klik untuk mengurutkan ${displayName}`}
    >
      <span className="font-semibold text-blue-600">{displayName}</span>
      {sortOrder && <i className={`text-gray-500 text-xs pi ${sortOrder === "asc" ? "pi-arrow-up" : "pi-arrow-down"}`}></i>}
    </div>
  );
};

const FilterOrderHeader = ({ displayName, id, openDropdown, setOpenDropdown, options = [], onSelectFilter, value }) => {
  const isOpen = openDropdown === id;
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const toggleDropdown = (event) => {
    const rect = event.target.getBoundingClientRect();
    setPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
    });
    setOpenDropdown(isOpen ? null : id);
  };

  const handleSelect = (selectedValue) => {
    const newValue = value === selectedValue ? null : selectedValue;
    onSelectFilter?.(newValue);
    setOpenDropdown(null);
  };

  const handleReset = () => {
    onSelectFilter?.(null);
    setOpenDropdown(null);
  };

  const dropdown = (
    <div
      className="absolute z-[9999] bg-white shadow-lg p-3"
      style={{
        top: position.top,
        left: position.left,
        minWidth: "250px",
        width: "250px",
      }}
    >
      <h5 className="font-semibold mb-2">Filter {displayName}</h5>
      <div className="flex flex-wrap gap-2 mb-3">
        {options.map((opt) => (
          <button
            key={opt.value}
            className={`btn ${opt.value === value ? "btn-primary" : "btn-outline-primary"} btn-sm`}
            type="button"
            onClick={() => handleSelect(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="flex flex-row w-full justify-content-between align-items-center gap-2">
        <button className="btn btn-warning btn-sm w-100" type="button" onClick={() => setOpenDropdown(null)}>
          Tutup
        </button>
        <button className="btn btn-danger btn-sm w-100" type="button" onClick={() => handleReset()}>
          Reset
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full relative">
      <div className="flex flex-row align-items-center w-full justify-content-between cursor-pointer select-none">
        <span className="font-semibold text-blue-600">
          {displayName} {value !== null && <i className="fa fa-circle text-xs ms-1"></i>}
        </span>
        <button className="btn btn-primary btn-sm" type="button" onClick={toggleDropdown}>
          <i className="fa fa-filter"></i>
        </button>
      </div>
      {isOpen && createPortal(dropdown, document.body)}
    </div>
  );
};

function formatToIDR(value) {
  if (value == null || isNaN(value)) return "-";
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(value);
}

export const ColumnSalaryCalculation = (
  ActionRenderer,
  handleSortOrder,
  openDropdown,
  setOpenDropdown,
  activeFilter,
  handleFilterProjectOrder,
  handleFilterSessionOrder
) => {
  const projectFilters = [
    { label: "Consumer", value: "CONSUMER" },
    { label: "Vendor", value: "VENDOR" },
    { label: "Kontraktor", value: "CONTRACTOR" },
    { label: "Pelaksana", value: "PELAKSANA" },
  ];

  const sessionFilters = Array.from({ length: 9 }, (_, i) => ({
    label: `${i}`,
    value: i,
  }));

  return [
    {
      headerName: "No",
      field: "no",
      width: 80,
      minWidth: 80,
      pinned: "left",
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Attendance Date",
      field: "attendanceDate",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Project",
      field: "sourceProject",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      headerComponent: FilterOrderHeader,
      headerComponentParams: {
        displayName: "Project",
        id: "project",
        openDropdown,
        setOpenDropdown,
        options: projectFilters,
        value: activeFilter?.project,
        onSelectFilter: (filter) => handleFilterProjectOrder?.(filter),
      },
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Project ID",
      field: "projectId",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      headerComponent: SortOrderHeader,
      headerComponentParams: {
        displayName: "Project ID",
        sortField: "project_id",
        setSortOrderParent: handleSortOrder,
      },
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Client Name",
      field: "clientName",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Worker Name",
      field: "workerName",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Skill",
      field: "skillName",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Order",
      field: "order",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Order Sukses",
      field: "orderSukses",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Total Tukang",
      field: "totalTukang",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueGetter: (params) => {
        const workerOnsite = params.data.workerOnsite ?? "-";
        const totalTukang = params.data.workerOrder ?? "-";
        return `${workerOnsite} / ${totalTukang}`;
      },
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Sesi",
      field: "sesi",
      width: 125,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      headerComponent: FilterOrderHeader,
      headerComponentParams: {
        displayName: "Sesi",
        id: "session",
        openDropdown,
        setOpenDropdown,
        options: sessionFilters,
        value: activeFilter?.session,
        onSelectFilter: (filter) => handleFilterSessionOrder?.(filter),
      },
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Upah Harian",
      field: "upahHarian",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Upah Insentif",
      field: "upahInsentif",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Upah Transport",
      field: "upahTransport",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Upah Keramik",
      field: "upahKeramik",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Upah Las",
      field: "upahLas",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },

    {
      headerName: "Fee Building",
      field: "feeBuilding",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Total Upah",
      field: "totalUpah",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (p) => `Rp ${p.value.toLocaleString()}`,
      headerComponent: SortOrderHeader,
      headerComponentParams: {
        displayName: "Total Upah",
        sortField: "total_upah",
        setSortOrderParent: handleSortOrder,
      },
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Tabungan / Asuransi",
      field: "insurance",
      width: 200,
      sortable: false,
      filter: false,
      suppressMovable: true,
      suppressSizeToFit: true,
      valueFormatter: (params) => formatToIDR(params.value),
      cellStyle: { fontWeight: "bold" },
    },
    {
      headerName: "Action",
      field: "action",
      width: 125,
      pinned: "right",
      cellRenderer: ActionRenderer,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressSizeToFit: true,
    },
  ];
};
